/*
 * AL THURAYAT - Admin Security Session
 * File: assets/at-admin-security-session.js
 *
 * Purpose:
 * - Central idle timeout for admin pages
 * - Configurable inactivity timeout: 10, 30, 90 minutes or 24 hours
 * - Warning 60 seconds before logout
 * - Activity synchronization across browser tabs
 * - Best-effort Supabase server logout
 * - Local session cleanup
 *
 * IMPORTANT:
 * - This file does NOT change login, permissions, accounting, inventory, or sales logic.
 * - Load it once from the shared admin shell/sidebar so it covers all admin pages.
 */

(function () {
  'use strict';

  /* =========================================================
     CONFIG
     ========================================================= */

  var SUPABASE_URL = 'https://xjqxifpxjvqrojhkmecg.supabase.co';
  var SUPABASE_ANON_KEY = 'sb_publishable_S23mhfstuHPOdKFPGqFxVg_SiIaTOA4';

  var SESSION_KEY = 'althurayat_platform_admin_session';
  var LAST_ACTIVITY_KEY = 'althurayat_platform_admin_last_activity';
  var LOGOUT_EVENT_KEY = 'althurayat_platform_admin_logout_event';

  var PREVIEW_THEME_ID = new URLSearchParams(window.location.search).get('preview_theme_id') || '';
  var LOGIN_URL = addPreviewTheme('/en-ae/pages/admin-login');

  var IDLE_TIMEOUT_MS = 30 * 60 * 1000;       // Default inactivity period
  var WARNING_BEFORE_MS = 60 * 1000;           // 60 seconds
  var CHECK_INTERVAL_MS = 1 * 1000;            // every second for an accurate 60-second warning
  var ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000;   // reduce localStorage writes

  function idleTimeoutMs() {
    var minutes=30;
    try {
      var current=readSession();
      var owner=String(current?.admin?.id||current?.user?.id||current?.admin?.email||current?.email||'');
      var prefs=owner?JSON.parse(localStorage.getItem('at_interface_v25_'+owner)):null;
      var value=Number(prefs?.idle);
      if([10,30,90,1440].includes(value))minutes=value;
      else if([5,15,20].includes(value))minutes=value===5?10:30;
    }catch(_){}
    return Math.min(24*60*60*1000,minutes*60*1000);
  }

  var CHANNEL_NAME = 'althurayat_admin_security_session';

  /* =========================================================
     STATE
     ========================================================= */

  var activeSession = readSession();
  var lastActivity = readLastActivity();
  var lastActivityWriteAt = 0;
  var warningVisible = false;
  var logoutStarted = false;
  var intervalId = null;
  var channel = null;
  var refreshingSession = null;

  /* =========================================================
     BOOT
     ========================================================= */

  if (!activeSession || !activeSession.access_token || !activeSession.admin) {
    return;
  }

  if (!lastActivity) {
    lastActivity = Date.now();
    writeLastActivity(lastActivity, true);
  }

  setupBroadcastChannel();
  setupActivityListeners();
  setupStorageSync();
  preservePreviewLinks();
  injectStyles();
  startWatcher();

  function addPreviewTheme(path) {
    if (!PREVIEW_THEME_ID) return path;
    var separator = path.indexOf('?') === -1 ? '?' : '&';
    return path + separator + 'preview_theme_id=' + encodeURIComponent(PREVIEW_THEME_ID);
  }

  function preservePreviewLinks() {
    if (!PREVIEW_THEME_ID) return;

    document.querySelectorAll('a[href^="/en-ae/pages/"]').forEach(function (link) {
      var href = String(link.getAttribute('href') || '');
      if (!href || href.indexOf('preview_theme_id=') !== -1) return;
      link.setAttribute('href', addPreviewTheme(href));
    });
  }

  /* =========================================================
     ACTIVITY
     ========================================================= */

  function setupActivityListeners() {
    var events = [
      'pointerdown',
      'keydown',
      'input',
      'change',
      'touchstart'
    ];

    events.forEach(function (eventName) {
      window.addEventListener(
        eventName,
        handleActivity,
        eventName === 'touchstart' ? { passive: true } : false
      );
    });

    // Scroll counts as activity, but is throttled heavily.
    window.addEventListener('scroll', handleActivity, { passive: true });

    // When returning to a visible tab, re-check immediately.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) {
        syncFromStorage();
        checkSession();
      }
    });

    window.addEventListener('focus', function () {
      syncFromStorage();
      checkSession();
    });
  }

  function handleActivity() {
    if (logoutStarted) return;

    var now = Date.now();

    // Update in-memory timestamp immediately.
    if (now > lastActivity) {
      lastActivity = now;
    }

    // Avoid excessive localStorage writes.
    if (now - lastActivityWriteAt >= ACTIVITY_WRITE_THROTTLE_MS) {
      writeLastActivity(now, false);
    }

    if (warningVisible) {
      hideWarning();
    }
  }

  function writeLastActivity(timestamp, force) {
    if (!force && Date.now() - lastActivityWriteAt < ACTIVITY_WRITE_THROTTLE_MS) {
      return;
    }

    lastActivityWriteAt = Date.now();
    lastActivity = Number(timestamp || Date.now());

    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(lastActivity));
    } catch (error) {
      // Keep in-memory fallback if localStorage is unavailable.
    }

    broadcast({
      type: 'activity',
      timestamp: lastActivity
    });
  }

  function readLastActivity() {
    try {
      var value = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
      return Number.isFinite(value) && value > 0 ? value : 0;
    } catch (error) {
      return 0;
    }
  }

  function syncFromStorage() {
    var stored = readLastActivity();
    if (stored > lastActivity) {
      lastActivity = stored;
    }
  }

  /* =========================================================
     WATCHER
     ========================================================= */

  function startWatcher() {
    checkSession();

    intervalId = window.setInterval(function () {
      syncFromStorage();
      checkSession();
    }, CHECK_INTERVAL_MS);
  }

  function checkSession() {
    if (logoutStarted) return;

    activeSession = readSession();

    // Session was removed from another tab.
    if (!activeSession || !activeSession.access_token || !activeSession.admin) {
      performLogout('session_removed', false);
      return;
    }

    var now = Date.now();
    var idleFor=now-lastActivity;
    var remaining=idleTimeoutMs()-idleFor;
    if(remaining<=0){performLogout('idle_timeout',true);return;}
    var expiresAt=Number(activeSession.expires_at||0);
    if(expiresAt&&expiresAt<100000000000)expiresAt*=1000;
    if(expiresAt&&now>=expiresAt){
      if(refreshingSession)return;
      if(activeSession.refresh_token&&window.ATAdminAccess?.current){
        var originalToken=activeSession.access_token,refreshTimer;
        var refresh=Promise.resolve().then(function(){return window.ATAdminAccess.current(true);}).then(function(result){if(logoutStarted)clearLocalSession();return result;});
        refreshingSession=Promise.race([refresh,new Promise(function(_,reject){refreshTimer=setTimeout(function(){reject(new Error('Session refresh timed out'));},15000);})]).then(function(){
          var latest=readSession(),expiry=Number(latest?.expires_at||0);if(expiry&&expiry<100000000000)expiry*=1000;
          if(logoutStarted)return;
          if(!latest?.access_token||!expiry||expiry<=Date.now())throw new Error('Session expired');
        }).catch(function(){if(readSession()?.access_token===originalToken)performLogout('token_expired',true);}).finally(function(){clearTimeout(refreshTimer);refreshingSession=null;});
        return;
      }
      performLogout('token_expired',true);return;
    }

    if (remaining <= WARNING_BEFORE_MS) {
      showWarning(remaining);
    } else if (warningVisible) {
      hideWarning();
    }
  }

  /* =========================================================
     WARNING MODAL
     ========================================================= */

  function showWarning(remainingMs) {
    warningVisible = true;

    var modal = getOrCreateWarningModal();
    var countdown = modal.querySelector('[data-at-session-countdown]');

    if (countdown) {
      countdown.textContent = formatRemaining(remainingMs);
    }

    modal.hidden = false;
    document.documentElement.classList.add('at-session-warning-open');
  }

  function hideWarning() {
    warningVisible = false;

    var modal = document.getElementById('atAdminSessionWarning');
    if (modal) {
      modal.hidden = true;
    }

    document.documentElement.classList.remove('at-session-warning-open');
  }

  function getOrCreateWarningModal() {
    var existing = document.getElementById('atAdminSessionWarning');
    if (existing) return existing;

    var wrapper = document.createElement('div');
    wrapper.id = 'atAdminSessionWarning';
    wrapper.className = 'at-session-warning';
    wrapper.hidden = true;
    wrapper.setAttribute('dir', 'rtl');
    wrapper.setAttribute('role', 'dialog');
    wrapper.setAttribute('aria-modal', 'true');
    wrapper.setAttribute('aria-labelledby', 'atSessionWarningTitle');

    wrapper.innerHTML =
      '<div class="at-session-warning__backdrop"></div>' +
      '<div class="at-session-warning__card">' +
        '<div class="at-session-warning__icon" aria-hidden="true">!</div>' +
        window.ATLocale.t("ui.3c842ccd1f14") +
        window.ATLocale.t("ui.8bd02dfe8adb") +
        '<div class="at-session-warning__countdown">' +
          window.ATLocale.t("ui.35d4bb9f96ee") +
          '<strong data-at-session-countdown>01:00</strong>' +
        '</div>' +
        '<div class="at-session-warning__actions">' +
          window.ATLocale.t("ui.19be196adb6f") +
          window.ATLocale.t("ui.0d69eff57e71") +
        '</div>' +
      '</div>';

    document.body.appendChild(wrapper);

    var continueButton = wrapper.querySelector('[data-at-session-continue]');
    var logoutButton = wrapper.querySelector('[data-at-session-logout]');

    if (continueButton) {
      continueButton.addEventListener('click', function () {
        var now = Date.now();
        writeLastActivity(now, true);
        hideWarning();
      });
    }

    if (logoutButton) {
      logoutButton.addEventListener('click', function () {
        performLogout('manual_from_warning', true);
      });
    }

    return wrapper;
  }

  function formatRemaining(milliseconds) {
    var seconds = Math.max(0, Math.ceil(milliseconds / 1000));
    var minutes = Math.floor(seconds / 60);
    var rest = seconds % 60;

    return String(minutes).padStart(2, '0') + ':' + String(rest).padStart(2, '0');
  }

  /* =========================================================
     LOGOUT
     ========================================================= */

  async function performLogout(reason, redirect) {
    if (logoutStarted) return;
    logoutStarted = true;

    if (intervalId) {
      window.clearInterval(intervalId);
      intervalId = null;
    }

    hideWarning();

    var sessionBeforeClear = readSession();

    // Notify all tabs first.
    var eventId = Date.now() + ':' + Math.random().toString(36).slice(2);

    try {
      localStorage.setItem(
        LOGOUT_EVENT_KEY,
        JSON.stringify({
          id: eventId,
          reason: reason || 'logout',
          at: Date.now()
        })
      );
    } catch (error) {}

    broadcast({
      type: 'logout',
      reason: reason || 'logout',
      eventId: eventId
    });

    // Remove browser session immediately.
    clearLocalSession();

    // Best-effort server-side Supabase sign-out.
    if (sessionBeforeClear && sessionBeforeClear.access_token) {
      try {
        await signOutFromSupabase(sessionBeforeClear.access_token);
      } catch (error) {
        // Never keep local access alive only because network sign-out failed.
      }
    }

    if (redirect !== false) {
      window.location.replace(
        LOGIN_URL +
        (LOGIN_URL.indexOf('?') === -1 ? '?session=' : '&session=') +
        encodeURIComponent(reason === 'idle_timeout' || reason === 'token_expired' ? 'expired' : 'logout')
      );
    }
  }

  function clearLocalSession() {
    try {
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(LAST_ACTIVITY_KEY);
    } catch (error) {}
  }

  async function signOutFromSupabase(accessToken) {
    var response = await fetch(SUPABASE_URL + '/auth/v1/logout', {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: 'Bearer ' + accessToken,
        'Content-Type': 'application/json'
      },
      body: '{}'
    });

    // 401 can occur when the token already expired; local logout is still valid.
    if (!response.ok && response.status !== 401) {
      throw new Error('Supabase logout failed with HTTP ' + response.status);
    }
  }

  /* =========================================================
     CROSS-TAB SYNC
     ========================================================= */

  function setupBroadcastChannel() {
    if (!('BroadcastChannel' in window)) return;

    try {
      channel = new BroadcastChannel(CHANNEL_NAME);

      channel.addEventListener('message', function (event) {
        var data = event && event.data ? event.data : {};

        if (data.type === 'activity') {
          var timestamp = Number(data.timestamp || 0);

          if (timestamp > lastActivity) {
            lastActivity = timestamp;

            if (warningVisible) {
              hideWarning();
            }
          }

          return;
        }

        if (data.type === 'logout') {
          receiveRemoteLogout(data.reason || 'logout');
        }
      });
    } catch (error) {
      channel = null;
    }
  }

  function broadcast(payload) {
    if (!channel) return;

    try {
      channel.postMessage(payload);
    } catch (error) {}
  }

  function setupStorageSync() {
    window.addEventListener('storage', function (event) {
      if (event.key === LAST_ACTIVITY_KEY && event.newValue) {
        var timestamp = Number(event.newValue || 0);

        if (timestamp > lastActivity) {
          lastActivity = timestamp;

          if (warningVisible) {
            hideWarning();
          }
        }

        return;
      }

      if (event.key === LOGOUT_EVENT_KEY && event.newValue) {
        var payload = {};

        try {
          payload = JSON.parse(event.newValue || '{}');
        } catch (error) {}

        receiveRemoteLogout(payload.reason || 'logout');
        return;
      }

      if (event.key === SESSION_KEY && !event.newValue) {
        receiveRemoteLogout('session_removed');
      }
    });
  }

  function receiveRemoteLogout(reason) {
    if (logoutStarted) return;
    logoutStarted = true;

    if (intervalId) {
      window.clearInterval(intervalId);
      intervalId = null;
    }

    hideWarning();
    clearLocalSession();

    window.location.replace(
      LOGIN_URL +
      (LOGIN_URL.indexOf('?') === -1 ? '?session=' : '&session=') +
      encodeURIComponent(reason === 'idle_timeout' || reason === 'token_expired' ? 'expired' : 'logout')
    );
  }

  /* =========================================================
     SESSION
     ========================================================= */

  function readSession() {
    try {
      return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
    } catch (error) {
      return null;
    }
  }

  /* =========================================================
     STYLES
     ========================================================= */

  function injectStyles() {
    if (document.getElementById('atAdminSecuritySessionStyles')) return;

    var style = document.createElement('style');
    style.id = 'atAdminSecuritySessionStyles';

    style.textContent =
      '.at-session-warning[hidden]{display:none!important}' +
      '.at-session-warning{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:20px;font-family:Cairo,"IBM Plex Sans Arabic",system-ui,sans-serif}' +
      '.at-session-warning__backdrop{position:absolute;inset:0;background:rgba(22,9,12,.68);backdrop-filter:blur(3px)}' +
      '.at-session-warning__card{position:relative;width:min(460px,94vw);background:#fffaf7;border:1px solid rgba(201,162,74,.38);border-radius:22px;box-shadow:0 28px 90px rgba(0,0,0,.30);padding:24px;text-align:center;color:#231719}' +
      '.at-session-warning__icon{width:54px;height:54px;margin:0 auto 12px;border-radius:50%;display:grid;place-items:center;background:#7c2231;color:#fff;font-size:28px;font-weight:950}' +
      '.at-session-warning__card h2{margin:0;color:#571723;font-size:23px;line-height:1.4}' +
      '.at-session-warning__card p{margin:10px 0 16px;color:#6f6266;line-height:1.8;font-size:14px}' +
      '.at-session-warning__countdown{display:flex;align-items:center;justify-content:space-between;gap:16px;background:#fbf5ef;border:1px solid #eadedf;border-radius:14px;padding:12px 14px;margin-bottom:16px}' +
      '.at-session-warning__countdown span{color:#6f6266;font-size:13px;font-weight:800}' +
      '.at-session-warning__countdown strong{direction:ltr;color:#7c2231;font-size:24px;letter-spacing:1px}' +
      '.at-session-warning__actions{display:grid;grid-template-columns:1fr 1fr;gap:10px}' +
      '.at-session-warning__actions button{min-height:46px;border:0;border-radius:12px;background:#7c2231;color:#fff;font:inherit;font-weight:950;cursor:pointer;padding:0 14px}' +
      '.at-session-warning__actions button.is-secondary{background:#fbf5ef;color:#571723;border:1px solid #eadedf}' +
      '.at-session-warning__actions button:hover{filter:brightness(.97)}' +
      '@media(max-width:560px){.at-session-warning__actions{grid-template-columns:1fr}.at-session-warning__card{padding:20px}}';

    document.head.appendChild(style);
  }

  /* =========================================================
     OPTIONAL PUBLIC API
     ========================================================= */

  document.addEventListener('click', function (event) {
    var target = event.target && event.target.closest
      ? event.target.closest('[data-admin-sidebar-logout]')
      : null;

    if (!target) return;

    event.preventDefault();
    performLogout('manual', true);
  });

  var legacyLogoutButton = document.getElementById('atAdminSessionLogout');
  if (legacyLogoutButton) {
    legacyLogoutButton.addEventListener('click', function () {
      performLogout('manual', true);
    });
  }

  var legacyContinueButton = document.getElementById('atAdminSessionContinue');
  if (legacyContinueButton) {
    legacyContinueButton.addEventListener('click', function () {
      writeLastActivity(Date.now(), true);
      hideWarning();
    });
  }

  window.ATAdminSecuritySession = {
    touch: function () {
      writeLastActivity(Date.now(), true);
      hideWarning();
    },

    logout: function () {
      return performLogout('manual', true);
    },

    getRemainingMs: function () {
      syncFromStorage();
      return Math.max(0, idleTimeoutMs() - (Date.now() - lastActivity));
    },

    getConfig: function () {
      return {
        idle_timeout_ms: idleTimeoutMs(),
        warning_before_ms: WARNING_BEFORE_MS
      };
    }
  };
})();


/* =========================================================
   INSTALLATION
   =========================================================

1) Create this Shopify Asset:
   assets/at-admin-security-session.js

2) Put the JavaScript above inside that Asset.

3) Load it ONCE from the shared admin component
   (recommended: snippets/althurayat-admin-sidebar.liquid)
   by adding this line near the end of the shared snippet:

   <script src="{{ 'at-admin-security-session.js' | asset_url }}" defer></script>

4) Do NOT add it separately to accounting, inventory, sales, etc.
   Loading it from the shared sidebar is intended to cover all admin pages.

5) Current policy:
   - Idle timeout: selected user preference, default 30 minutes
   - Warning: 60 seconds before logout

========================================================= */



